import { FC, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useMethods } from '@lhb/hook';
import V2Container from '@/common/components/Data/V2Container';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import SkeletonDetail from '@/common/components/Skeleton/SkeletonDetail';
import Header from './TaskDetailHeader';
import { getExpansionTaskDetail } from '@/common/api/expandStore/expansiontask';
import { getApprovalDetail } from '@/common/api/expandStore/approveworkbench';
import TaskDetail from './TaskDetail';
import styles from '@/common/components/business/ExpandStore/TaskDetailDrawer/index.module.less';

interface Props {
  /** 是否打开 */
  open: boolean;
  /** 控制是否打开 */
  setOpen: Function;
  /** 拓店任务Id */
  id: any;
  /** 是否隐藏操作按钮 */
  hideOperate?: boolean;
  // 刷新外层调用方处理（通常是页面）
  outterRefresh?: Function;
  /** 详情成功打开 */
  onLoadSuccess?: Function;
  /** 详情不存在或加载失败 */
  onLoadFailure?: Function;
}

/** 拓店任务：加盟（三津汤包）-详情抽屉 */
const TaskDetailDrawer: FC<Props> = ({
  open,
  setOpen,
  id,
  hideOperate = false,
  outterRefresh,
  onLoadSuccess,
  onLoadFailure,
}) => {
  const [, setMainHeight] = useState<number>(0);
  const [detail, setDetail] = useState<any>({}); // 拓店任务详情信息
  const [loading, setLoading] = useState<boolean>(false); // 加载中
  const [refresh, setRefresh] = useState<boolean>(false); // 刷新
  const [approvalDetail, setApprovalDetail] = useState<any>(null);// 审批详情
  const [loadingSk, setLoadingSk] = useState<boolean>(true); // 骨架屏

  useEffect(() => {
    if (!id && !refresh) return;
    setLoadingSk(true);
    open && methods.getTaskDetail();
    // 非打开抽屉时初次请求，是操作后刷新时，同时回调刷新外层
    refresh && outterRefresh && outterRefresh();
  }, [id, refresh, open]);

  useEffect(() => {
    if (!open) setDetail({}); // 解决打开下个详情时请求的还是前个任务id的问题
  }, [open]);

  useEffect(() => {
    methods.getApprovalDetailInfo();
  }, [detail?.approvalId]);

  const methods = useMethods({
    /** 获取拓店任务详情 */
    getTaskDetail() {
      setLoading(true);
      getExpansionTaskDetail({ id }).then((res) => {
        if (onLoadFailure && !res?.id) {
          onLoadFailure && onLoadFailure();
          return;
        }
        setDetail(res);
        setRefresh(false); // 刷新结束
        onLoadSuccess && onLoadSuccess(res);
      }, () => {
        onLoadFailure && onLoadFailure();
      }).finally(() => {
        setLoading(false);
        setLoadingSk(false);
      });
    },
    /** 获取审批详情 */
    async getApprovalDetailInfo() {
      if (!detail?.approvalId) {
        setApprovalDetail({});
        return;
      }
      const data = await getApprovalDetail({ id: detail?.approvalId });
      setApprovalDetail(data);
    },
    handleClose() {
      setOpen(false);
    },
    handleRefresh() {
      setRefresh(true);
    },
    handleMainHeight(height: number) {
      setMainHeight(height);
    },
  });

  return (
    <V2Drawer
      bodyStyle={{ padding: 0, }}
      destroyOnClose
      open={open}
      onClose={methods.handleClose}
    >
      {
        loadingSk
          ? <SkeletonDetail />
          : <V2Container
            // 容器上下padding 32， 所以减去就是64
            style={{ height: 'calc(100vh - 64px)' }}
            emitMainHeight={methods.handleMainHeight}
            className={styles.container}
            extraContent={{
              top: <Header
                detail={detail}
                hideOperate={hideOperate}
                onRefresh={methods.handleRefresh}
              />,
            }}
          >
            <div className='pb-16 pl-40 pr-40'>
              <Spin spinning={loading}>
                <TaskDetail
                  detail={detail}
                  aprDetail={approvalDetail}
                  refresh={methods.handleRefresh}
                  hideOperate={hideOperate}
                />
              </Spin>
            </div>
          </V2Container>
      }
    </V2Drawer>
  );
};

export default TaskDetailDrawer;
